import * as THREE from 'three';
import { Player } from './player.js';
import { BossPhase1 } from './enemies/bossPhase1.js';
import { BossPhase2 } from './enemies/bossPhase2.js';
import { FragmentManager } from './fragments.js';
import { createProjectilePool } from './projectile.js';
import { circleVsAabb, circleVsCircle, segmentVsAabbs } from '../engine/collision.js';
import {
  SANDBOX_TEMPLATE,
  GRAYBOX_VARIANT_ORDER,
} from './rooms/templates.js';
import { HUD } from './hud.js';
import { FragmentDraftUI } from './ui/fragmentDraft.js';
import { showPickupNotification } from './ui/pickupNotification.js';
import { makeRng } from '../engine/rng.js';
import { VARIANTS } from './metrics.js';
import { ENEMY_STATS } from '../data/enemies.data.js';
import { SlashFxSystem } from './fx/slashFx.js';
import { EnemyDropPreviewSystem } from './fx/enemyDropPreview.js';
import { spawnAmmoPickup, spawnRoomWeaponPickup, spawnWeaponPickup } from './pickupSpawning.js';
import { updateGame, updateProjectiles } from './runtimeUpdate.js';
import { isBossRoomActive, updateFogOfWar } from './fogOfWarRuntime.js';
import { attachSeedDebugHandle, attachSlashFxDebugHandle } from './debugHandlesRuntime.js';
import { createFogOfWarMesh, ensureHitFlash, roomSeedFor, syncSlashFxDebug } from './coreRuntime.js';
import { selectOutlineTargetEnemy, updateEnemyTargetOutlines } from './outlineRuntime.js';
import { clearAmmoPickups, clearPlayerMines, clearShards, clearWeaponPickups } from './cleanupRuntime.js';
import { spawnMeleeSlashFx } from './slashFxRuntime.js';
import { addEnemyByType, findSafeSummonSpawn, isSpawnPointClear, orientEnemyOnSpawn, radiusForEnemyType, setSandboxDebugPanelVisible } from './enemyRuntime.js';
import { debugSpawnPoint, handleSandboxHotkeys, spawnBossPhase, spawnSummonedEnemy } from './sandboxRuntime.js';
import { alertNearbyEnemies, composeSpawnForRoom, pauseGame, quitRun, resumeGame } from './gameplayStateRuntime.js';
import { onPlayerHit, onPlayerHitEnemy } from './combatFxRuntime.js';
import { flashHit, onBossFire, onEnemyKilled, onPlayerDeath } from './combatLifecycleRuntime.js';
import { selectHoveredDrop, spawnShard, updateDropTooltip, updateShards } from './dropRuntime.js';
import { explodePlayerProjectile, smartEquipFromPickup, updateAmmoPickups, updatePlayerMines, updateWeaponPickups } from './pickupCombatRuntime.js';
import { flashCircle, schedulePlayerBurstShot, spawnPlayerMine, toast, updateBurstShots, updateEnemyStatusEffects, worldToScreen } from './combatUtilityRuntime.js';
import { illuminationAt, spawnEnemyProjectile, spawnPlayerProjectile } from './projectileRuntime.js';
import { advanceRoom, scheduleVictory } from './roomFlowRuntime.js';
import { buildCurrentRoom, variantForRoom } from './roomBuildRuntime.js';
import { spawnEnemiesForRoom } from './enemyRoomSpawnRuntime.js';
import {
  applySandboxInfiniteAmmo,
  clearEnemiesAndProjectiles,
  isPlayerHiddenFromEnemies,
  setAssetOrchestrator,
  setAssets,
  setSandboxGodMode,
  startRun,
  startSandbox,
  startSession,
  triggerBossPreload,
} from './sessionRuntime.js';
import { spawnRuntime } from './spawnRuntime.js';
import {
  resetExitPressure,
  registerExitObjectiveKill,
  updateExitPressureSpawns,
  updateExitPressureTimer,
} from './runtime/encounter/exitPressure.js';
import { addCharacterProgressKills, getCharacterById, getSelectedCharacterId } from './characters.js';
import { AtmosphereDustSystem } from './atmosphereRuntime.js';
import { clearLootPickups, generateEnemyDrop, roomClearBonus, updateLootPickups } from './lootRuntime.js';
import { createTutorialRuntime } from './tutorialRuntime.js';
import { minimap } from './ui/minimap.js';
import { comboRuntime } from './comboRuntime.js';
import { pickupRuntime, resolveWeaponForGroundPickup } from './pickupRuntime.js';

const TOTAL_ROOMS = 10;
const SANDBOX_TOTAL_ROOMS = 1;
const FRAGMENT_AFTER_ROOMS = [3, 6, 9]; // open draft after clearing these room indices
const BOSS_ROOMS = { 5: 1, 10: 2 }; // 1-indexed room -> boss phase
const FOG_OF_WAR_RADIUS = 15.0;
const FOG_OF_WAR_FEATHER = 6.0;
const FOG_OF_WAR_OUTER_RADIUS = 80;
const FOG_OF_WAR_OPACITY = 0.78;
const FOG_OF_WAR_ENABLED = false;
const SEEN_WEAPON_PICKUPS_STORAGE_KEY = 'oneman.seenWeaponPickups.v1';
const PLAYER_CURSOR_LINE_MIN_DIST = 0.12;
const PLAYER_CURSOR_LINE_MAX_DIST = 22;

function enemyTypeFromEnemy(enemy) {
  if (!enemy) return 'drone';
  if (enemy.isBoss) return 'boss';
  const name = String(enemy.constructor?.name || '').toLowerCase();
  if (name.includes('enforcer')) return 'enforcer';
  if (name.includes('zealot')) return 'zealot';
  if (name.includes('minmon')) return 'minmon';
  if (name.includes('zilua')) return 'zilua';
  return 'drone';
}

// Central game controller. Owns the scene objects and per-frame flow.
export class Game {
  constructor({ scene, cameraRig, audio, input, assets, hud, menuCtl, postprocess = null, wallThicknessScale = 1 }) {
    this.scene = scene;
    this.cameraRig = cameraRig;
    this.audio = audio;
    this.input = input;
    this.assets = assets;
    this.hud = hud || new HUD();
    this.menuCtl = menuCtl;
    this.postprocess = postprocess;

    this.state = 'menu'; // menu | playing | paused | draft | gameover | victory
    this.runMode = 'run'; // run | sandbox
    this.totalRooms = TOTAL_ROOMS;
    this.roomIndex = 0; // 0-based
    this.rng = makeRng();
    this.fragments = new FragmentManager(this.rng);
    this.draftUI = new FragmentDraftUI();
    this.draftUI.setAudio?.(audio);
    showPickupNotification.setPausedResolver?.(() => this.state !== 'playing');

    this.player = null;
    this.room = null;
    this.enemies = [];
    this.shards = []; // explosive shards from Fracture Core
    this.ammoPickups = [];
    this.weaponPickups = [];
    this.lootPickups = [];
    this.playerMines = [];
    this._pendingBurstShots = [];
    this.projectiles = createProjectilePool(scene, 256);
    this.slashFx = new SlashFxSystem(scene);
    this.enemyDropPreview = new EnemyDropPreviewSystem(scene);
    this.timeScale = 1;
    this._timeScaleTimer = 0;
    this._roomElapsedSeconds = 0;
    this._exitPressureActive = false;
    this._exitPressureSpawnClock = 0;
    this._exitPressureSpawnCount = 0;
    this._exitObjectiveKills = 0;
    this._exitObjectiveKillTarget = 0;
    this._exitObjectiveCompleted = false;
    this._deathSequenceTimer = 0;
    this._deathSequenceDelay = 1.0;
    this._deathGameOverTriggered = false;

    // Props loaded from kitbash FBX
    this.kitbashProps = [];

    this._kitbashLoaded = false;
    this._playerFbx = null;
    this._boss01Fbx = null;
    this._boss02Fbx = null;
    this._assetOrchestrator = null;
    // Tracks which boss rooms have already had their FBX load triggered,
    // so we don't keep re-firing `ensureBossNN()` every frame.
    this._bossPreloadFired = { 1: false, 2: false };

    this._nextRoomPending = false;
    this._draftPending = false;
    this._hitFlashEl = null;
    this._hitFxTmpV = new THREE.Vector3();
    this._debugPanelEl = document.getElementById('debug-panel');
    this._sandboxInfiniteAmmoInput = document.getElementById('sandbox-infinite-ammo');
    if (this._sandboxInfiniteAmmoInput) {
      this._sandboxInfiniteAmmoInput.addEventListener('change', () => this._applySandboxInfiniteAmmo());
    }
    this._sandboxGodMode = false;
    this._outlineTargetEnemy = null;
    this._outlineRaycaster = new THREE.Raycaster();
    this._dropHoverRaycaster = new THREE.Raycaster();
    this._dropHoverMeta = new Map();
    this._slashFxDebug = {
      enabled: false,
      freezeFrame: false,
      forcedRow: null,
      forcedProgress: null,
      showAnchor: false,
      slowMoFx: 1,
      wireframeArc: false,
    };
    this._fogOfWar = this._createFogOfWarMesh();
    this._atmosphereDust = new AtmosphereDustSystem(scene);
    this._playerCursorLinePulse = 0;
    this._playerCursorLinePoints = [new THREE.Vector3(), new THREE.Vector3()];
    this._initPlayerCursorLine();
    this._attachSlashFxDebugHandle();
    this._ensureHitFlash();
    this._spawnRoomClearedHandled = false;
    this._roomEntrySnapshot = null;
    this._sessionSeed = 0;
    this._selectedCharacterId = getSelectedCharacterId();
    this._lastRoomSeed = 0;
    this._bossRooms = BOSS_ROOMS;
    this.wallThicknessScale = Math.max(0.1, Number.isFinite(wallThicknessScale) ? wallThicknessScale : 1);
    this._seenWeaponPickups = this._loadSeenWeaponPickups();
    this.spawnRuntime = spawnRuntime;
    this.minimap = minimap;
    this.tutorialRuntime = createTutorialRuntime({ game: this });
    pickupRuntime.init(this);
    this._attachSeedDebugHandle();
  }

  _initPlayerCursorLine() {
    const geometry = new THREE.BufferGeometry().setFromPoints(this._playerCursorLinePoints);
    const material = new THREE.LineBasicMaterial({
      color: 0x57e8ff,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.visible = false;
    line.renderOrder = 22;
    this.scene.add(line);
    this._playerCursorLine = line;
  }

  _setPlayerCursorLineVisible(visible) {
    if (!this._playerCursorLine) return;
    this._playerCursorLine.visible = !!visible;
  }

  _updatePlayerCursorLine(dt) {
    const line = this._playerCursorLine;
    if (!line || !this.player || !this.input) return;
    if (this.state !== 'playing' || !this.player.alive) {
      line.visible = false;
      return;
    }

    const startX = this.player.x;
    const startZ = this.player.z;
    const toCursorX = this.input.mouse.worldX - startX;
    const toCursorZ = this.input.mouse.worldZ - startZ;
    const dist = Math.hypot(toCursorX, toCursorZ);
    if (!Number.isFinite(dist) || dist < PLAYER_CURSOR_LINE_MIN_DIST) {
      line.visible = false;
      return;
    }

    const clampedDist = Math.min(dist, PLAYER_CURSOR_LINE_MAX_DIST);
    const invDist = 1 / Math.max(0.0001, dist);
    const endX = startX + toCursorX * invDist * clampedDist;
    const endZ = startZ + toCursorZ * invDist * clampedDist;
    const y = 0.12;
    this._playerCursorLinePoints[0].set(startX, y, startZ);
    this._playerCursorLinePoints[1].set(endX, y, endZ);
    line.geometry.setFromPoints(this._playerCursorLinePoints);

    this._playerCursorLinePulse += dt;
    const pulse = 0.5 + 0.5 * Math.sin(this._playerCursorLinePulse * 10);
    const distFade = THREE.MathUtils.clamp(1 - clampedDist / PLAYER_CURSOR_LINE_MAX_DIST, 0.25, 1);
    line.material.opacity = (0.4 + pulse * 0.38) * distFade;
    line.visible = true;
  }

  _onRoomClearedFromSpawnRuntime(roomNumber) {
    if (this.runMode !== 'run' || !this.room || this._spawnRoomClearedHandled) return;
    if (this._exitPressureActive && !this._exitObjectiveCompleted) return;
    this._spawnRoomClearedHandled = true;
    this._roomClearBonus(roomNumber);
    this.room.unlockDoor();
    this.audio.play('roomCleared');
    this.audio.play('doorOpen');
    if (roomNumber === this.totalRooms) {
      this._scheduleVictory();
      return;
    }
    this.toast('ROOM CLEARED');
  }

  _createFogOfWarMesh() {
    return createFogOfWarMesh(this, {
      outerRadius: FOG_OF_WAR_OUTER_RADIUS,
      innerRadius: FOG_OF_WAR_RADIUS,
      feather: FOG_OF_WAR_FEATHER,
      opacity: FOG_OF_WAR_OPACITY,
    });
  }

  _isBossRoomActive() {
    return isBossRoomActive(this, BOSS_ROOMS);
  }

  _updateFogOfWar() {
    updateFogOfWar(this, {
      enabledFlag: FOG_OF_WAR_ENABLED,
      bossRooms: BOSS_ROOMS,
    });
  }

  _attachSlashFxDebugHandle() {
    attachSlashFxDebugHandle(this);
  }

  _attachSeedDebugHandle() {
    attachSeedDebugHandle(this);
  }

  _roomSeedFor(roomNum) {
    return roomSeedFor(this, roomNum);
  }

  _syncSlashFxDebug() {
    syncSlashFxDebug(this);
  }

  _ensureHitFlash() {
    ensureHitFlash(this);
  }

  // Partial-safe: only keys explicitly present on the argument are updated.
  // Pass `null` to clear an asset, omit a key to leave it untouched.
  setAssets(patch = {}) {
    setAssets(this, patch);
  }

  setAssetOrchestrator(orchestrator) {
    setAssetOrchestrator(this, orchestrator);
  }

  // Fire-and-forget: kick off a background load for the requested boss phase.
  // Silently no-op if the orchestrator isn't wired or the phase is unknown.
  _triggerBossPreload(phase) {
    triggerBossPreload(this, phase);
  }

  startRun(seed = Date.now() >>> 0) {
    startRun(this, seed, TOTAL_ROOMS);
    comboRuntime.init((count) => this.hud.updateCombo(count));
  }

  startSandbox(seed = Date.now() >>> 0) {
    startSandbox(this, seed, SANDBOX_TOTAL_ROOMS);
    comboRuntime.init((count) => this.hud.updateCombo(count));
  }

  _startSession(seed) {
    startSession(this, seed);
    if (this.player?.applyCharacterProfile) {
      this.player.applyCharacterProfile(getCharacterById(this._selectedCharacterId));
      this.player.reset(this.room?.playerSpawn?.x || 0, this.room?.playerSpawn?.z || 0);
    }
    comboRuntime.init((count) => this.hud.updateCombo(count));
    this._deathSequenceTimer = 0;
    this._deathGameOverTriggered = false;
    this.hud.showObjectiveUpdate(
      'Searching for escape route... Please proceed through the upper path!',
      'Press SPACE to dash. Press E / F to equip nearby weapons.',
      3200
    );
  }

  _applySandboxInfiniteAmmo() {
    applySandboxInfiniteAmmo(this);
  }

  isPlayerHiddenFromEnemies() {
    return isPlayerHiddenFromEnemies(this);
  }

  _setSandboxGodMode(on) {
    setSandboxGodMode(this, on);
  }

  _clearEnemiesAndProjectiles() {
    clearEnemiesAndProjectiles(this);
  }

  _selectOutlineTargetEnemy() {
    return selectOutlineTargetEnemy(this);
  }

  _updateEnemyTargetOutlines() {
    updateEnemyTargetOutlines(this);
  }

  _clearShards() {
    clearShards(this);
  }

  _clearAmmoPickups() {
    clearAmmoPickups(this);
  }

  _clearWeaponPickups() {
    clearWeaponPickups(this);
  }

  _clearPlayerMines() {
    clearPlayerMines(this);
  }

  _spawnGroundWeaponPickup(weaponData, position) {
    return pickupRuntime.spawnPickup(weaponData, position);
  }

  _spawnGroundWeaponPickupById(weaponId, position, rarity = null) {
    const weaponData = resolveWeaponForGroundPickup(weaponId, rarity);
    if (!weaponData) return null;
    return this._spawnGroundWeaponPickup(weaponData, position);
  }

  _updateGroundWeaponPickups(dt) {
    pickupRuntime.update(dt);
  }

  _clearGroundWeaponPickups() {
    pickupRuntime.clear();
  }

  spawnMeleeSlashFx({ weapon, hand = 'right', player, aimX, aimZ, when = 0.2 } = {}) {
    spawnMeleeSlashFx(this, { weapon, hand, player, aimX, aimZ, when });
  }

  _buildCurrentRoom() {
    buildCurrentRoom(this, {
      sandboxTemplate: SANDBOX_TEMPLATE,
      variants: VARIANTS,
    });
    this._rebuildAtmosphereForRoom();
    this._resetExitPressure();
  }

  _captureRoomEntrySnapshot() {
    const p = this.player;
    if (!p) return;
    const cloneHand = (hand) => {
      const s = p.getHandState(hand);
      return {
        weaponId: s.weaponId,
        rarityOverride: s.rarityOverride ?? null,
        ammoInMag: Number.isFinite(s.ammoInMag) ? s.ammoInMag : Number.POSITIVE_INFINITY,
        ammoReserve: Number.isFinite(s.ammoReserve) ? s.ammoReserve : Number.POSITIVE_INFINITY,
        magCapacity: Number.isFinite(s.magCapacity) ? s.magCapacity : Number.POSITIVE_INFINITY,
        reserveCapacity: Number.isFinite(s.reserveCapacity) ? s.reserveCapacity : Number.POSITIVE_INFINITY,
        reloadDuration: Number(s.reloadDuration) || 0,
      };
    };
    this._roomEntrySnapshot = {
      hp: Number(p.hp) || p.maxHp,
      medkits: Number(p.medkits) || 0,
      left: cloneHand('left'),
      right: cloneHand('right'),
    };
  }

  _applyRoomEntrySnapshot(snapshot) {
    const p = this.player;
    if (!p || !snapshot) return false;
    const currentFragments = new Set(p.fragments);
    const applyHand = (hand, data) => {
      if (!data) return;
      p.hands[hand] = {
        weaponId: data.weaponId,
        rarityOverride: data.rarityOverride ?? null,
        ammoInMag: data.ammoInMag,
        ammoReserve: data.ammoReserve,
        magCapacity: data.magCapacity,
        reserveCapacity: data.reserveCapacity,
        reloadDuration: data.reloadDuration,
        reloadTime: 0,
        fireCd: 0,
        triggerHeld: false,
        shotsSinceTrigger: 0,
      };
    };
    p.hp = Math.max(1, Math.min(p.maxHp, Number(snapshot.hp) || p.maxHp));
    p.medkits = Math.max(0, Number(snapshot.medkits) || 0);
    applyHand('left', snapshot.left);
    applyHand('right', snapshot.right);
    p.fragments = currentFragments;
    p.fragmentState = p.fragmentState || {};
    p.dashCd = 0;
    p.dashTime = 0;
    p.invuln = 0;
    p.fireCd = 0;
    p.stamina = Number.isFinite(p.maxStamina) ? p.maxStamina : p.stamina;
    return true;
  }

  restartCurrentRoom() {
    if (!this.player) return;
    this.state = 'playing';
    this.spawnRuntime?.stop();
    this._clearEnemiesAndProjectiles();
    this._clearShards();
    this._clearAmmoPickups();
    this._clearWeaponPickups();
    this._clearGroundWeaponPickups();
    this._clearLootPickups();
    this._clearPlayerMines();
    if (this.room) {
      this.room.dispose();
      this.room = null;
    }
    this._buildCurrentRoom();
    if (!this._applyRoomEntrySnapshot(this._roomEntrySnapshot)) {
      this.player.reset(this.room?.playerSpawn?.x || 0, this.room?.playerSpawn?.z || 0);
    }
    this.player.x = this.room?.playerSpawn?.x || 0;
    this.player.z = this.room?.playerSpawn?.z || 0;
    this.player.group.position.set(this.player.x, 0, this.player.z);
    this.cameraRig.setFollow(this.player.x, this.player.z);
    this.cameraRig.setLead(this.player.x, this.player.z);
    this.hud.setFragments([...this.player.fragments]);
    this.hud.update(this.player, this.roomIndex, this.totalRooms);
  }

  _rebuildAtmosphereForRoom() {
    this._atmosphereDust?.rebuildForRoom(this.room);
  }

  _updateAtmosphere(dt) {
    this._atmosphereDust?.update(dt, this.player);
    this.room?.updatePlayerLightGlow?.(this.player, dt);
  }

  _resetExitPressure() {
    resetExitPressure(this);
  }

  _variantForRoom(roomNum) {
    return variantForRoom(roomNum, GRAYBOX_VARIANT_ORDER, this._sessionSeed);
  }

  _spawnAmmoPickup(x, z, amount = 6) {
    spawnAmmoPickup({
      scene: this.scene,
      dropHoverMeta: this._dropHoverMeta,
      ammoPickups: this.ammoPickups,
      x,
      z,
      amount,
    });
  }

  _spawnWeaponPickup(x, z, weaponId) {
    spawnWeaponPickup({
      scene: this.scene,
      dropHoverMeta: this._dropHoverMeta,
      weaponPickups: this.weaponPickups,
      rng: this.rng,
      x,
      z,
      weaponId,
    });
  }

  _spawnRoomWeaponPickup(roomNum) {
    spawnRoomWeaponPickup(this, roomNum);
  }

  _radiusForEnemyType(type) {
    return radiusForEnemyType(type);
  }

  _isSpawnPointClear(x, z, radius, { minPlayerDistance = 0, ignoreEnemy = null } = {}) {
    return isSpawnPointClear(this, x, z, radius, { minPlayerDistance, ignoreEnemy });
  }

  _findSafeSummonSpawn(type, originX, originZ, opts = {}) {
    return findSafeSummonSpawn(this, type, originX, originZ, opts);
  }

  _spawnEnemiesForRoom(roomNum, variantId = null) {
    spawnEnemiesForRoom(this, roomNum, variantId, BOSS_ROOMS);
  }

  _setSandboxDebugPanelVisible(show) {
    setSandboxDebugPanelVisible(this, show);
  }

  _addEnemyByType(type, x, z) {
    return addEnemyByType(this, type, x, z);
  }

  _orientEnemyOnSpawn(enemy) {
    orientEnemyOnSpawn(this, enemy);
  }

  spawnSummonedEnemy(type, opts = {}) {
    return spawnSummonedEnemy(this, type, opts);
  }

  _spawnBossPhase(phase, x, z) {
    const BossCtor = phase === 2 ? BossPhase2 : BossPhase1;
    const boss = new BossCtor({
      scene: this.scene,
      x,
      z,
      audio: this.audio,
      fbx: phase === 2 ? this._boss02Fbx : this._boss01Fbx,
    });
    this.enemies.push(boss);
    return boss;
  }

  _debugSpawnPoint(distance = 8.5) {
    return debugSpawnPoint(this, distance);
  }

  _handleSandboxHotkeys() {
    handleSandboxHotkeys(this);
  }

  _updateExitPressureTimer(dt) {
    updateExitPressureTimer(this, dt);
  }

  _updateExitPressureSpawns(dt) {
    updateExitPressureSpawns(this, dt);
  }

  alertNearbyEnemies(sourceX, sourceZ, radius = 8.5) {
    alertNearbyEnemies(this, sourceX, sourceZ, radius);
  }

  _composeSpawn(roomNum, variantId = null) {
    return composeSpawnForRoom(this, roomNum, variantId);
  }

  pause() {
    pauseGame(this);
  }
  resume() { resumeGame(this); }

  setPaused(paused) {
    if (paused) {
      this.pause();
      this.audio?.setMuted?.(true);
      return;
    }
    this.resume();
    this.audio?.setMuted?.(false);
  }

  quitRun() {
    quitRun(this, TOTAL_ROOMS);
  }

  onPlayerHit(_n) {
    comboRuntime.onHit();
    onPlayerHit(this);
  }

  onPlayerHitEnemy(enemy, projectile = null) {
    onPlayerHitEnemy(this, enemy, projectile);
  }

  /**
   * Camera kick when a boss fires. Phase 2 (final boss) hits harder than
   * phase 1 (miniboss). Direction is the boss aim vector so the camera
   * lurches in the direction of the shot.
   */
  onBossFire(boss, { intensityScale = 1 } = {}) {
    onBossFire(this, boss, { intensityScale });
  }

  _flashHit() {
    flashHit(this);
  }

  onPlayerDeath() {
    comboRuntime.onDeath();
    this._deathSequenceTimer = this._deathSequenceDelay;
    this._deathGameOverTriggered = false;
    this.state = 'dying';
  }

  _updateDeathSequence(dt) {
    if (this.state !== 'dying') return;
    this.player?.update(dt, this.input, this.room?.walls || [], this);
    this.slashFx?.update(dt, this.cameraRig?.camera);
    this.enemyDropPreview?.update(dt);
    this._updateAtmosphere(dt);
    this._updateFogOfWar();
    this.hud.update(this.player, this.roomIndex, this.totalRooms);
    this.hud.hideDropTooltip();
    this.cameraRig.setFollow(this.player.x, this.player.z);
    this.cameraRig.setLead(this.player.x, this.player.z);
    this._deathSequenceTimer = Math.max(0, this._deathSequenceTimer - dt);
    if (this._deathSequenceTimer <= 0 && !this._deathGameOverTriggered) {
      this._deathGameOverTriggered = true;
      onPlayerDeath(this);
    }
  }

  onEnemyKilled(enemy) {
    this.spawnRuntime?.onEnemyKilled?.(enemy);
    registerExitObjectiveKill(this);
    addCharacterProgressKills(1);
    this.menuCtl?.onEnemyKilled?.(1);
    comboRuntime.onKill();
    onEnemyKilled(this, enemy);
    const enemyType = enemyTypeFromEnemy(enemy);
    this._generateEnemyDrop(enemyType, { x: enemy.x, z: enemy.z });
    if (enemyType === 'boss' && this.roomIndex + 1 === 10) {
      const hand = this.fragments.drawHand(1);
      const picked = hand[0] ? this.fragments.resolveHand(hand, hand[0].id, this.player) : null;
      if (picked) {
        this.hud.setFragments([...this.player.fragments]);
        this.audio.play('fragmentPick', { rate: 1.08 });
        this.toast('+ BONUS FRAGMENT');
      }
    }
  }

  _spawnShard(x, z) {
    spawnShard(this, x, z);
  }

  _selectHoveredDrop() {
    return selectHoveredDrop(this);
  }

  _updateDropTooltip() {
    updateDropTooltip(this);
  }

  _updateShards(dt) {
    updateShards(this, dt);
  }

  _updateAmmoPickups(dt) {
    updateAmmoPickups(this, dt);
  }

  _smartEquipFromPickup(weaponId) {
    const result = smartEquipFromPickup(this, weaponId);
    this._notifyNewWeaponPickup(weaponId);
    return result;
  }

  _loadSeenWeaponPickups() {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(SEEN_WEAPON_PICKUPS_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((id) => typeof id === 'string' && id.length > 0));
    } catch {
      return new Set();
    }
  }

  _saveSeenWeaponPickups() {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        SEEN_WEAPON_PICKUPS_STORAGE_KEY,
        JSON.stringify([...this._seenWeaponPickups].sort()),
      );
    } catch {
      // Storage can fail in private mode or quota pressure; ignore safely.
    }
  }

  _weaponPickupDisplayName(weaponId) {
    const raw = String(weaponId || '').trim();
    if (!raw) return 'Unknown Weapon';
    return raw
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }

  _notifyNewWeaponPickup(weaponId) {
    const id = String(weaponId || '').trim();
    if (!id) return false;
    if (this._seenWeaponPickups.has(id)) return false;
    this._seenWeaponPickups.add(id);
    this._saveSeenWeaponPickups();
    this.toast(`New weapon obtained: ${this._weaponPickupDisplayName(id)}`);
    return true;
  }

  _updateWeaponPickups(dt) {
    updateWeaponPickups(this, dt);
  }

  _generateEnemyDrop(enemyType, position) {
    generateEnemyDrop(this, enemyType, position);
  }

  _roomClearBonus(roomNumber) {
    roomClearBonus(this, roomNumber);
  }

  _updateLootPickups(dt) {
    updateLootPickups(this, dt);
  }

  _clearLootPickups() {
    clearLootPickups(this);
  }

  _explodePlayerProjectile(p, x, z) {
    const sourceWeaponId = p?.sourceWeaponId;
    if (sourceWeaponId === 'jigoku_launcher' || sourceWeaponId === 'tenrai_mortar' || sourceWeaponId === 'bakuha_mines') {
      this.audio?.play?.('launcherExplode', { pitchJitter: 0.03 });
    }
    explodePlayerProjectile(this, p, x, z);
  }

  _updatePlayerMines(dt) {
    updatePlayerMines(this, dt);
  }

  _updateEnemyStatusEffects(dt) {
    updateEnemyStatusEffects(this, dt);
  }

  _updateBurstShots(dt) {
    updateBurstShots(this, dt);
  }

  _flashCircle(x, z, r) {
    flashCircle(this, x, z, r);
  }

  _worldToScreen(x, y, z) {
    return worldToScreen(this, x, y, z);
  }

  toast(msg) { toast(this, msg); }

  schedulePlayerBurstShot(delay, cb) {
    schedulePlayerBurstShot(this, delay, cb);
  }

  spawnPlayerMine(x, z, opts = {}) {
    spawnPlayerMine(this, x, z, opts);
  }

  spawnPlayerProjectile(x, z, dx, dz, opts = {}) {
    spawnPlayerProjectile(this, x, z, dx, dz, opts);
  }

  spawnEnemyProjectile(x, z, dx, dz, opts = {}) {
    spawnEnemyProjectile(this, x, z, dx, dz, opts);
  }

  // Illumination factor for emissive / basic-material visuals (vision cones,
  // projectiles). Returns 1.0 inside the player's light radius and 0.2 well
  // outside, with a short smooth falloff at the edge.
  illuminationAt(x, z) {
    return illuminationAt(this, x, z);
  }

  _updateProjectiles(dt) {
    updateProjectiles(this, dt);
  }

  transitionPostprocessToRoom(roomNumber, duration = 1.5) {
    this.postprocess?.transitionToRoom?.(roomNumber, duration);
  }

  _triggerEnemyDropPreviewAtPlayer() {
    this.enemyDropPreview?.triggerAtPlayer(this.player);
  }

  _clearEnemyDropPreview() {
    this.enemyDropPreview?.clear();
  }

  update(dt) {
    updateGame(this, dt);
  }

  _scheduleVictory() {
    scheduleVictory(this);
  }

  async _advanceRoom() {
    await advanceRoom(this, FRAGMENT_AFTER_ROOMS);
  }
  setSelectedCharacter(characterId) {
    this._selectedCharacterId = getCharacterById(characterId).id;
  }
}


